import { useContext } from "react";
import { Link, useLocation } from "react-router-dom";

import { AuthContext } from "@/contexts/authContext";
import { useProviderProfile } from "@/hooks/provider/useProviderProfile";
import { useIdentity } from "@/hooks/useIdentity";
import { useLatestOrder } from "@/hooks/useLatestOrder";
import {
  CONSUMER_HEALTH_DATA_PRIVACY,
  LEGAL_DISCLAIMER_US,
  PRIVACY_POLICY,
  PROVIDER_AGREEMENT,
  TERMS_OF_SERVICE,
} from "@/pages/constants";

export type FooterVariant = "default" | "provider-revamp";

type FooterProps = {
  variant?: FooterVariant;
};

const legalDisclaimerCA = `The Evvy Vaginal Health Test - Canada is for people with vaginas who want to be empowered with information about their microbiome. The Evvy test in Canada is intended exclusively for wellness purposes. Evvy cannot provide you with medical advice or diagnose you with any disease or condition in Canada. Evvy does not provide medical advice and is not intended to replace the advice of your doctor.`;

interface FooterContentProps {
  disclaimer: string;
  isProvider?: boolean;
  variant?: FooterVariant;
}

const AuthenticatedFooter = ({ variant = "default" }: FooterProps) => {
  const { latestOrder } = useLatestOrder();
  const { isProvider } = useIdentity();
  const [providerProfile] = useProviderProfile(isProvider);

  const getLegalDisclaimer = () => {
    const providerCountryIsUS =
      providerProfile?.clinic_country === "United States";
    const providerCountryIsCA = providerProfile?.clinic_country === "Canada";

    if (isProvider) {
      if (providerCountryIsCA) {
        return legalDisclaimerCA;
      } else if (providerCountryIsUS) {
        return LEGAL_DISCLAIMER_US;
      }
    }
    if (latestOrder?.country === "Canada") {
      return legalDisclaimerCA;
    }
    return LEGAL_DISCLAIMER_US;
  };

  return (
    <FooterContent
      disclaimer={getLegalDisclaimer()}
      isProvider={isProvider}
      variant={variant}
    />
  );
};

const FooterContent = ({
  disclaimer,
  isProvider,
  variant = "default",
}: FooterContentProps) => {
  const location = useLocation();
  const copyrightText = `Copyright © ${new Date().getFullYear()} Evvy. All rights reserved`;
  const policyLinkStyle = "underline underline-offset-2";

  if (variant === "provider-revamp") {
    return (
      <footer className="border-t border-evvy-gray-100 bg-evvy-light-cream px-6 md:px-16 pt-6 pb-8 text-sm leading-[1.45] text-evvy-black">
        <div className="md:flex md:flex-row">
          <div className="max-w-xl pb-4 md:pb-0">
            <img
              src="/images/evvy-logo-text.svg"
              alt="Evvy"
              className="mb-5 h-8 w-auto"
              loading="lazy"
            />
            <div>{disclaimer}</div>
            <div className="mt-6 text-xs md:text-sm">{copyrightText}</div>
          </div>

          <div className="flex flex-col md:ml-auto items-end">
            <div className="flex items-end mt-auto">
              <div className={policyLinkStyle}>
                <a href={TERMS_OF_SERVICE}>Terms of Service</a>
              </div>
              <div className="px-2">|</div>
              <div className={policyLinkStyle}>
                <a href={PRIVACY_POLICY}>Privacy Policy</a>
              </div>
              <div className="px-2">|</div>
              <div className={policyLinkStyle}>
                <a href={PROVIDER_AGREEMENT}>Provider Agreement</a>
              </div>
              <div className="px-2">|</div>
              <div className={policyLinkStyle}>
                <a href={CONSUMER_HEALTH_DATA_PRIVACY}>
                  Consumer Health Data Privacy
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  const path = location.pathname;

  const fdaDisclaimerPatterns = [
    /^\/care\/$/, // /care/
    /^\/care\/shop\/treatment\/op-4\/?$/, // /care/shop/treatment/op-4/
    /^\/care\/consults\/[^\/]+\/treatment\/?$/, // /care/consults/{consult uid}/treatment/
    /^\/care\/consults\/[^\/]+\/refills\/?$/, // /care/consults/{consult uid}/refills/
    /^\/treatments\/consults\/[^\/]+\/treatment\/?$/, // /treatments/consults/{consult uid}/treatment/
  ];

  const shouldShowFDADisclaimerContainer = fdaDisclaimerPatterns.some(
    (pattern) => pattern.test(path)
  );

  return (
    <footer className="bg-evvy-black text-evvy-cream px-6 md:px-16 pt-6 pb-8 text-sm z-0 align-bottom">
      <div className="mb-4">
        <Link to="/tests" className="pointer-events-auto">
          <img
            className="h-8 mt-2 sm:h-10"
            src="/images/evvy-logo-text-white.svg"
            loading="lazy"
            alt="Evvy Text Logo"
          />
        </Link>
      </div>

      <div className="md:flex md:flex-row">
        <div className="max-w-xl pb-4 md:pb-0">
          <div>{disclaimer}</div>
        </div>

        {/* Portal target for mobile FDA disclaimer */}
        {shouldShowFDADisclaimerContainer && (
          <div id="fda-disclaimer-mobile-portal" className="block md:hidden" />
        )}

        <div className="flex flex-col md:ml-auto items-end">
          {/* Portal target for desktop FDA disclaimer */}
          {shouldShowFDADisclaimerContainer && (
            <div
              id="fda-disclaimer-desktop-portal"
              className="hidden md:block"
            />
          )}

          <div className="flex items-end mt-auto">
            <div className="hidden md:block">{copyrightText}</div>
            <div className="px-2 hidden md:block"></div>
            <div className={policyLinkStyle}>
              <a href={TERMS_OF_SERVICE}>Terms of Service</a>
            </div>
            <div className="px-2">|</div>
            <div className={policyLinkStyle}>
              <a href={PRIVACY_POLICY}>Privacy Policy</a>
            </div>
            <div className="px-2">|</div>
            {isProvider && (
              <>
                <div className={policyLinkStyle}>
                  <a href={PROVIDER_AGREEMENT}>Provider Agreement</a>
                </div>
                <div className="px-2">|</div>
              </>
            )}
            <div className={policyLinkStyle}>
              <a href={CONSUMER_HEALTH_DATA_PRIVACY}>
                Consumer Health Data Privacy
              </a>
            </div>
          </div>
        </div>

        <div className="block md:hidden mt-2">{copyrightText}</div>
      </div>
    </footer>
  );
};

const Footer = ({ variant = "default" }: FooterProps) => {
  const { authenticated } = useContext(AuthContext);

  if (authenticated) {
    return <AuthenticatedFooter variant={variant} />;
  }

  return <FooterContent disclaimer={LEGAL_DISCLAIMER_US} variant={variant} />;
};

export default Footer;
